import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import campaignService from '../services/campaignService.js';
import recipientService from '../services/recipientService.js';
import analyticsService from '../services/analyticsService.js';
// import jobsMonitorService from '../../jobs-monitor/services/jobsMonitorService.js';
import './CampaignDetail.css';

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [campaignJobs, setCampaignJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // info, recipients, results
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadCampaignData();
  }, [campaignId]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [campaignData, recipientsData, statsData, jobsData] = await Promise.all([
        campaignService.getCampaign(campaignId),
        recipientService.getRecipients(campaignId),
        analyticsService.getCampaignStats(campaignId),
        analyticsService.getCampaignJobs(campaignId).catch(() => [])
      ]);

      setCampaign(campaignData);
      setRecipients(recipientsData);
      setStats(statsData);
      setCampaignJobs(jobsData);
    } catch (err) {
      console.error('Error loading campaign data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleStartCampaign = async () => {
    if (!window.confirm('Start this campaign? This will create jobs for all recipients.')) {
      return;
    }

    setActionLoading(true);
    try {
      await campaignService.startCampaign(campaignId);
      alert('Campaign started successfully!');
      await loadCampaignData();
    } catch (err) {
      console.error('Error starting campaign:', err);
      alert(`Failed to start campaign: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePauseCampaign = async () => {
    setActionLoading(true);
    try {
      await campaignService.pauseCampaign(campaignId);
      alert('Campaign paused');
      await loadCampaignData();
    } catch (err) {
      console.error('Error pausing campaign:', err);
      alert(`Failed to pause campaign: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeCampaign = async () => {
    setActionLoading(true);
    try {
      await campaignService.resumeCampaign(campaignId);
      alert('Campaign resumed');
      await loadCampaignData();
    } catch (err) {
      console.error('Error resuming campaign:', err);
      alert(`Failed to resume campaign: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      await campaignService.deleteCampaign(campaignId);
      alert('Campaign deleted');
      navigate('/marketing');
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert(`Failed to delete campaign: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      draft: 'status-draft',
      scheduled: 'status-scheduled',
      active: 'status-active',
      completed: 'status-completed',
      paused: 'status-paused'
    };
    return statusMap[status] || 'status-draft';
  };

  if (loading) {
    return (
      <div className="campaign-detail-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="campaign-detail-page">
        <div className="error-message">
          ⚠️ {error || 'Campaign not found'}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/marketing')}>
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="campaign-detail-page">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary" onClick={() => navigate('/marketing')}>
            ← Back
          </button>
          <h1>{campaign.name}</h1>
          <span className={`status-badge ${getStatusBadgeClass(campaign.status)}`}>
            {campaign.status}
          </span>
        </div>
        <div className="header-actions">
          {campaign.status === 'draft' && (
            <button
              className="btn btn-primary"
              onClick={handleStartCampaign}
              disabled={actionLoading}
            >
              {actionLoading ? 'Starting...' : '▶️ Start Campaign'}
            </button>
          )}
          {campaign.status === 'active' && (
            <button
              className="btn btn-secondary"
              onClick={handlePauseCampaign}
              disabled={actionLoading}
            >
              {actionLoading ? 'Pausing...' : '⏸️ Pause'}
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              className="btn btn-primary"
              onClick={handleResumeCampaign}
              disabled={actionLoading}
            >
              {actionLoading ? 'Resuming...' : '▶️ Resume'}
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={handleDeleteCampaign}
            disabled={actionLoading}
          >
            🗑️ Delete
          </button>
        </div>
      </div>

      <div className="campaign-content">
        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Campaign Info
          </button>
          <button
            className={`tab ${activeTab === 'recipients' ? 'active' : ''}`}
            onClick={() => setActiveTab('recipients')}
          >
            Recipients ({recipients.length})
          </button>
          <button
            className={`tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            Results
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'info' && (
            <div className="info-tab">
              <div className="info-section">
                <h3>Campaign Details</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <strong>Name:</strong> {campaign.name}
                  </div>
                  <div className="info-item">
                    <strong>Type:</strong> {campaign.type === 'both' ? 'LinkedIn & Email' : campaign.type}
                  </div>
                  <div className="info-item">
                    <strong>Status:</strong> {campaign.status}
                  </div>
                  <div className="info-item">
                    <strong>Created:</strong> {formatDate(campaign.createdAt)}
                  </div>
                  {campaign.scheduledAt && (
                    <div className="info-item">
                      <strong>Scheduled:</strong> {formatDate(campaign.scheduledAt)}
                    </div>
                  )}
                  {campaign.sentAt && (
                    <div className="info-item">
                      <strong>Sent:</strong> {formatDate(campaign.sentAt)}
                    </div>
                  )}
                </div>
              </div>

              {campaign.description && (
                <div className="info-section">
                  <h3>Description</h3>
                  <p>{campaign.description}</p>
                </div>
              )}

              {(campaign.type === 'email' || campaign.type === 'both') && (
                <div className="info-section">
                  <h3>Email Content</h3>
                  <div className="content-preview">
                    <div className="content-item">
                      <strong>Subject:</strong> {campaign.subject}
                    </div>
                    <div className="content-item">
                      <strong>Body:</strong>
                      <div className="content-body">{campaign.content}</div>
                    </div>
                  </div>
                </div>
              )}

              {(campaign.type === 'linkedin' || campaign.type === 'both') && (
                <div className="info-section">
                  <h3>LinkedIn Message</h3>
                  <div className="content-preview">
                    <div className="content-body">{campaign.linkedinMessage}</div>
                  </div>
                </div>
              )}

              {stats && (
                <div className="info-section">
                  <h3>Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Total:</span>
                      <span className="stat-value">{stats.stats.total}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Sent:</span>
                      <span className="stat-value">{stats.stats.sent}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Delivered:</span>
                      <span className="stat-value">{stats.stats.delivered}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Opened:</span>
                      <span className="stat-value">{stats.stats.opened}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Clicked:</span>
                      <span className="stat-value">{stats.stats.clicked}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Replied:</span>
                      <span className="stat-value">{stats.stats.replied}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Failed:</span>
                      <span className="stat-value">{stats.stats.failed}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'recipients' && (
            <div className="recipients-tab">
              <div className="recipients-header">
                <h3>Recipients ({recipients.length})</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/marketing/${campaignId}/add-recipients`)}
                >
                  ➕ Add Recipients
                </button>
              </div>
              {recipients.length === 0 ? (
                <div className="no-recipients">
                  <p>No recipients added yet.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/marketing/${campaignId}/add-recipients`)}
                  >
                    Add Recipients
                  </button>
                </div>
              ) : (
                <div className="recipients-table-container">
                  <table className="recipients-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>LinkedIn</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((recipient) => (
                        <tr key={recipient.recipientId}>
                          <td>{recipient.email || '-'}</td>
                          <td>{recipient.linkedinUrl || '-'}</td>
                          <td>{recipient.firstName} {recipient.lastName}</td>
                          <td>{recipient.company || '-'}</td>
                          <td>{recipient.title || '-'}</td>
                          <td>
                            <span className={`status-badge status-${recipient.status}`}>
                              {recipient.status}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-danger btn-small"
                              onClick={async () => {
                                if (window.confirm('Remove this recipient?')) {
                                  try {
                                    await recipientService.deleteRecipient(campaignId, recipient.recipientId);
                                    await loadCampaignData();
                                  } catch (err) {
                                    alert(`Failed to remove recipient: ${err.message}`);
                                  }
                                }
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'results' && (
            <div className="results-tab">
              <h3>Campaign Results</h3>
              {campaignJobs.length === 0 ? (
                <div className="no-results">
                  <p>No jobs created yet. Start the campaign to create jobs.</p>
                </div>
              ) : (
                <div className="jobs-list">
                  <p>Jobs linked to this campaign: {campaignJobs.length}</p>
                  <p className="info-text">
                    Job results are processed by workers. Check the Jobs Monitor for detailed job status.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;

